import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  Video,
  Sparkles,
  Archive,
  Trash2
} from 'lucide-react';
import { getPlatformIcon } from '@/lib/platform-utils';
import { TimeWithTimezone } from '@/components/timezone';

interface ContentGalleryProps {
  contents: any[];
  loading?: boolean;
  onContentClick?: (content: any) => void;
  title?: string;
  description?: string;
  emptyMessage?: string;

  showCampaign?: boolean;
}

const getContentImage = (content: any): string | null => {
  try {
    // First check the new direct image_url field for better performance
    if (content.image_url) return content.image_url;

    // Check other direct properties
    if (content.thumbnail_url) return content.thumbnail_url;

    // Fallback to parsing content_json for backward compatibility
    if (content.content_json) {
      const contentJson = typeof content.content_json === 'string'
        ? JSON.parse(content.content_json)
        : content.content_json;

      if (contentJson.image_url) return contentJson.image_url;
      if (contentJson.thumbnail_url) return contentJson.thumbnail_url;

      // Check for images array
      if (contentJson.images && Array.isArray(contentJson.images) && contentJson.images.length > 0) {
        return contentJson.images[0];
      }

      // Check for media array
      if (contentJson.media && Array.isArray(contentJson.media)) {
        const imageMedia = contentJson.media.find((m: any) => m.type === 'image');
        if (imageMedia?.url) return imageMedia.url;
      }
    }

    // Check content field as fallback
    if (content.content && typeof content.content === 'string') {
      try {
        const parsed = JSON.parse(content.content);
        if (parsed.image_url) return parsed.image_url;
        if (parsed.thumbnail_url) return parsed.thumbnail_url;
        if (parsed.media?.length > 0) {
          const imageMedia = parsed.media.find((m: any) =>
            m.type === 'image' || m.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
          );
          if (imageMedia?.url) return imageMedia.url;
        }
      } catch {
        // If parsing fails, continue to next check
      }
    }
  } catch (e) {
    console.error('Error extracting image from content:', e);
  }

  return null;
};

const hasVideo = (content: any): boolean => {
  try {
    // First check the new direct video_url field for better performance
    if (content.video_url) return true;

    // Fallback to parsing content_json for backward compatibility
    if (content.content_json) {
      const contentJson = typeof content.content_json === 'string'
        ? JSON.parse(content.content_json)
        : content.content_json;

      if (contentJson.video_url) return true;
      if (contentJson.media && Array.isArray(contentJson.media)) {
        return contentJson.media.some((m: any) => m.type === 'video');
      }
    }

    // Check content field as fallback
    if (content.content && typeof content.content === 'string') {
      try {
        const parsed = JSON.parse(content.content);
        if (parsed.video_url) return true;
        if (parsed.media?.length > 0) {
          return parsed.media.some((m: any) =>
            m.type === 'video' || m.url?.match(/\.(mp4|mov|avi|webm)$/i)
          );
        }
      } catch {
        // If parsing fails, continue
      }
    }
  } catch (e) {
    console.error('Error checking for video in content:', e);
  }

  return false;
};

const getTextPreview = (content: any): string | null => {
  try {
    // First check the new direct text_preview field for better performance
    if (content.text_preview) {
      return content.text_preview;
    }

    // Fallback to extracting from content_json for backward compatibility
    if (content.content_json) {
      const contentJson = typeof content.content_json === 'string'
        ? JSON.parse(content.content_json)
        : content.content_json;

      // Try to extract meaningful text content from various structures
      if (contentJson.content) {
        // Strip HTML tags and limit length
        const textContent = contentJson.content.replace(/<[^>]*>/g, '').trim();
        if (textContent) {
          return textContent.length > 300 ? textContent.substring(0, 297) + '...' : textContent;
        }
      }

      if (contentJson.caption) {
        return contentJson.caption.length > 300 ? contentJson.caption.substring(0, 297) + '...' : contentJson.caption;
      }

      if (contentJson.text) {
        return contentJson.text.length > 300 ? contentJson.text.substring(0, 297) + '...' : contentJson.text;
      }

      // Check for social post structure
      if (contentJson.posts && Array.isArray(contentJson.posts) && contentJson.posts.length > 0) {
        const firstPost = contentJson.posts[0];
        if (firstPost.text) {
          return firstPost.text.length > 300 ? firstPost.text.substring(0, 297) + '...' : firstPost.text;
        }
        if (firstPost.caption) {
          return firstPost.caption.length > 300 ? firstPost.caption.substring(0, 297) + '...' : firstPost.caption;
        }
      }
    }

    // Check content field as fallback
    if (content.content && typeof content.content === 'string') {
      try {
        const parsed = JSON.parse(content.content);
        if (parsed.text) {
          return parsed.text.length > 300 ? parsed.text.substring(0, 297) + '...' : parsed.text;
        }
        if (parsed.caption) {
          return parsed.caption.length > 300 ? parsed.caption.substring(0, 297) + '...' : parsed.caption;
        }
        if (parsed.content) {
          const textContent = parsed.content.replace(/<[^>]*>/g, '').trim();
          if (textContent) {
            return textContent.length > 300 ? textContent.substring(0, 297) + '...' : textContent;
          }
        }
      } catch {
        // If parsing fails, continue
      }
    }
  } catch (e) {
    console.error('Error extracting text preview from content:', e);
  }

  return null;
};

const getContentTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    'BLOG_POST': 'Blog Post',
    'SOCIAL_MEDIA': 'Social Media',
    'EMAIL': 'Email',
    'VIDEO': 'Video',
    'PODCAST': 'Podcast',
    'INFOGRAPHIC': 'Infographic',
    'LANDING_PAGE': 'Landing Page',
    'AD': 'Advertisement',
    'PRESENTATION': 'Presentation',
    'CONTENT': 'Content'
  };
  return typeLabels[type] || type;
};

const ContentGallery: React.FC<ContentGalleryProps> = ({
  contents,
  loading = false,
  onContentClick,
  title,
  description,
  emptyMessage = 'No content available',

  showCampaign = false
}) => {
  // Use fixed Tailwind classes for grid columns - max 2 columns for dashboard use
  // Mobile stays 1 column until tablet (768px+) for better mobile experience
  const getGridClass = () => {
    return `grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {title && (
          <div>
            <Skeleton className="h-7 w-48 mb-2" />
            {description && <Skeleton className="h-4 w-96" />}
          </div>
        )}
        <div className={getGridClass()}>
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden border-blue-200/50 bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30">
              <Skeleton className="h-40 w-full" />
              <div className="p-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
                <div className="flex gap-2 mt-3">
                  <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-8 w-full mt-3" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!contents || contents.length === 0) {
    return (
      <div className="space-y-4">
        {title && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
          </div>
        )}
        <div className="flex items-center justify-center py-8 px-4 bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30 rounded-xl border border-blue-200/50 shadow-sm">
          <div className="text-center max-w-md">
            <Sparkles className="h-10 w-10 text-blue-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600">{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {title && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
        </div>
      )}
      
      <div className={getGridClass()}>
        {contents.map(content => {
          const contentImage = getContentImage(content);
          const hasVideoContent = hasVideo(content);
          const textPreview = getTextPreview(content);
          const platformIcon = getPlatformIcon(content.platform || content.platform_key);
          
          return (
            <div
              key={content.id}
              className="group relative bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer flex flex-col border border-blue-200/50"
              onClick={() => onContentClick?.(content)}
            >
              {/* Image/Video Preview */}
              {(contentImage || hasVideoContent) ? (
                <div className="relative h-40 overflow-hidden rounded-t-xl bg-gradient-to-br from-gray-100 to-gray-200">
                  {contentImage ? (
                    <>
                      <img 
                        src={contentImage} 
                        alt={content.title || content.name || 'Content'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.classList.add('flex', 'items-center', 'justify-center');
                            parent.innerHTML = '<div class="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm"><svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }
                        }}
                      />
                      {hasVideoContent && (
                        <div className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm rounded-full">
                          <Video className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </>
                  ) : hasVideoContent ? (
                    <div className="flex items-center justify-center h-full bg-gradient-to-br from-purple-100 to-blue-200">
                      <div className="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm">
                        <Video className="h-8 w-8 text-purple-600" />
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Status Badge Overlay */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    {content.display_status === 'ARCHIVED' ? (
                      <div className="px-2 py-1 bg-gray-600/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          <Archive className="h-3 w-3" /> Archived
                        </span>
                      </div>
                    ) : content.display_status === 'DELETED' ? (
                      <div className="px-2 py-1 bg-red-600/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Deleted
                        </span>
                      </div>
                    ) : content.display_status === 'PUBLISHED' || content.is_published ? (
                      <div className="px-2 py-1 bg-green-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          ✓ Published
                        </span>
                      </div>
                    ) : content.display_status === 'SCHEDULED' || content.is_scheduled ? (
                      <div className="px-2 py-1 bg-orange-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          ⏰ Scheduled
                        </span>
                      </div>
                    ) : content.display_status === 'PROCESSING' ? (
                      <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Publishing...</span>
                      </div>
                    ) : content.display_status === 'FAILED' ? (
                      <div className="px-2 py-1 bg-red-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Failed</span>
                      </div>
                    ) : content.display_status === 'CANCELLED' ? (
                      <div className="px-2 py-1 bg-gray-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Unpublished</span>
                      </div>
                    ) : content.display_status === 'DRAFT' || content.status === 'GENERATING' ? (
                      <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Draft</span>
                      </div>
                    ) : content.display_status === 'READY' || content.is_ready ? (
                      <div className="px-2 py-1 bg-gray-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Ready</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                /* Fallback header with gradient */
                <div className="relative h-28 bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center rounded-t-xl">
                  <div className="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm">
                    <FileText className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  {/* Status Badge for non-image content */}
                  <div className="absolute top-3 left-3">
                    {content.display_status === 'ARCHIVED' ? (
                      <div className="px-2 py-1 bg-gray-600/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          <Archive className="h-3 w-3" /> Archived
                        </span>
                      </div>
                    ) : content.display_status === 'DELETED' ? (
                      <div className="px-2 py-1 bg-red-600/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Deleted
                        </span>
                      </div>
                    ) : content.display_status === 'PUBLISHED' || content.is_published ? (
                      <div className="px-2 py-1 bg-green-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">✓ Published</span>
                      </div>
                    ) : content.display_status === 'SCHEDULED' || content.is_scheduled ? (
                      <div className="px-2 py-1 bg-orange-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">⏰ Scheduled</span>
                      </div>
                    ) : content.display_status === 'PROCESSING' ? (
                      <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Publishing...</span>
                      </div>
                    ) : content.display_status === 'FAILED' ? (
                      <div className="px-2 py-1 bg-red-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Failed</span>
                      </div>
                    ) : content.display_status === 'CANCELLED' ? (
                      <div className="px-2 py-1 bg-gray-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Unpublished</span>
                      </div>
                    ) : content.display_status === 'DRAFT' || content.status === 'GENERATING' ? (
                      <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Draft</span>
                      </div>
                    ) : content.display_status === 'READY' || content.is_ready ? (
                      <div className="px-2 py-1 bg-gray-500/90 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs font-medium">Ready</span>
                      </div>
                    ) : null}
                  </div>
                  
                  {/* Platform icon */}
                  <div className="absolute top-3 right-3">
                    {platformIcon}
                  </div>
                </div>
              )}

              {/* Content Details */}
              <div className="relative p-4 flex-1 flex flex-col">
                {/* Title */}
                <h3 className="text-base font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300 line-clamp-2 mb-2">
                  {content.title || content.name || content.caption || 'Untitled Content'}
                </h3>

                {/* Text Preview */}
                {textPreview && (
                  <p className="text-xs text-gray-600 line-clamp-2 mb-2 leading-relaxed">
                    {textPreview}
                  </p>
                )}

                {/* Content Type Badge */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {content.content_type && (
                    <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-medium shadow-sm">
                      {getContentTypeLabel(content.content_type)}
                    </Badge>
                  )}
                  {!contentImage && !hasVideoContent && platformIcon && (
                    <div className="text-gray-500">{platformIcon}</div>
                  )}
                </div>

                {/* Details Section */}
                <div className="space-y-2 mb-4 flex-1">
                  {/* Campaign */}
                  {showCampaign && content.campaign && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 font-medium">Campaign</span>
                      <span className="text-blue-600 font-medium truncate max-w-[100px]">
                        {content.campaign.name}
                      </span>
                    </div>
                  )}

                  {/* Date */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 font-medium">
                      {content.is_published ? 'Published' :
                       content.is_scheduled ? 'Scheduled' :
                       'Created'}
                    </span>
                    <TimeWithTimezone
                      date={content.effective_publish_time || content.created_at}
                      showRelative={true}
                      showTimezone={false}
                      size="sm"
                      showTooltip={true}
                      relativeMode="smart"
                      preferRelative={true}
                      relativeOptions={{
                        shortFormat: true,
                        absoluteThreshold: 7
                      }}
                      className="text-right max-w-[100px] truncate"
                    />
                  </div>
                </div>

                {/* View/Edit Button */}
                <button className="w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs">
                  View/Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContentGallery;